// InstrumentLogo — square brand tile with a 1–3 char monogram. Stands in
// for the actual SVG logos Razorpay uses on their dashboard (we don't ship
// their copyrighted bank/network marks). The tile is sized + tinted enough
// that operators can scan a column at a glance without confusing
// instruments.
//
// Why colored tiles instead of plain text: at 8pt-equivalent monogram size,
// the colour difference is the primary affordance. The lighter shade
// behind the letter softens it against light/dark themes without burning
// out either side.

interface Props {
  monogram: string;
  color: string;
  size?: number;
}

export function InstrumentLogo({ monogram, color, size = 28 }: Props) {
  // Blend with the background a little so the tile reads as a "logo space"
  // not a solid swatch. The letter sits on the brand colour at full
  // saturation for readability.
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: tintBackground(color),
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        border: "1px solid var(--hair, rgba(0,0,0,0.06))",
      }}
      aria-hidden="true"
    >
      <span
        style={{
          fontFamily: "var(--font-sans, system-ui)",
          fontSize: monogram.length >= 3 ? 9 : 11,
          fontWeight: 700,
          color,
          letterSpacing: "0.02em",
          lineHeight: 1,
        }}
      >
        {monogram}
      </span>
    </div>
  );
}

// tintBackground — 14% alpha of the brand colour, rendered against the
// surface. Razorpay uses real logos so they don't need this; we're
// faking the same "logo-shaped chip" affordance with the tint.
function tintBackground(hex: string): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, 0.12)`;
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
